import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../App";
import { ChevronDown } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_02be9fd0-b3b1-41a7-adf4-8c9054bc694e/artifacts/z5cygm86_Cover%20Khatira.jpg";

export default function HomePage() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  return (
    <div style={{ background: "#0D0704" }}>
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="hero-section">
        <img
          src={HERO_IMAGE}
          alt="KHATIRA Cover"
          className="hero-img"
          data-testid="hero-image"
        />
        <div className="hero-overlay" />

        <div className="hero-content">
          {/* Logo — KHATIRA in Gold, Cinzel font, precisely centered */}
          <div
            className="fade-in-up delay-100"
            data-testid="hero-logo"
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "1.75rem",
            }}
          >
            <span
              className="font-logo"
              style={{
                fontSize: "clamp(3.5rem, 13vw, 10.5rem)",
                fontWeight: 600,
                letterSpacing: "0.22em",
                paddingLeft: "0.22em",
                color: "#D4B878",
                textTransform: "uppercase",
                lineHeight: 0.95,
                textShadow: "0 0 80px rgba(212, 184, 120, 0.22), 0 6px 40px rgba(8,4,2,0.6)",
                display: "block",
              }}
            >
              KHATIRA
            </span>
          </div>

          {/* Tagline */}
          <p
            className="font-body fade-in-up delay-200"
            data-testid="hero-tagline"
            style={{
              fontSize: "clamp(0.9rem, 2vw, 1.25rem)",
              color: "rgba(212, 184, 120, 0.75)",
              fontWeight: 300,
              letterSpacing: "0.28em",
              marginBottom: "0.85rem",
              textTransform: "uppercase",
            }}
          >
            {t("hero_tagline")}
          </p>

          {/* Subtitle */}
          <p
            className="font-body fade-in-up delay-300"
            data-testid="hero-subtitle"
            style={{
              fontSize: "clamp(0.85rem, 1.4vw, 1rem)",
              color: "rgba(237, 224, 196, 0.6)",
              fontWeight: 300,
              maxWidth: "520px",
              margin: "0 auto 2.5rem",
              lineHeight: 1.8,
              letterSpacing: "0.02em",
            }}
          >
            {t("hero_subtitle")}
          </p>

          {/* AI Model badges */}
          <div
            className="fade-in-up delay-350"
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "center",
              marginBottom: "2.75rem",
              flexWrap: "wrap",
            }}
          >
            {["Gemini Nano Banana", "Sora 2"].map((model) => (
              <span
                key={model}
                style={{
                  padding: "0.3rem 1rem",
                  border: "1px solid rgba(201, 168, 76, 0.25)",
                  color: "rgba(201, 168, 76, 0.65)",
                  borderRadius: "999px",
                  fontSize: "0.7rem",
                  letterSpacing: "0.18em",
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 400,
                  textTransform: "uppercase",
                }}
              >
                {model}
              </span>
            ))}
          </div>

          {/* CTA */}
          <button
            data-testid="hero-cta-btn"
            className="btn-primary fade-in-up delay-400"
            style={{
              padding: "1rem 3.5rem",
              fontSize: "0.85rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
            onClick={() => navigate("/studio")}
          >
            {t("hero_cta")}
          </button>
        </div>

        {/* Scroll cue */}
        <div
          style={{
            position: "absolute",
            bottom: "2.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.4rem",
            color: "rgba(201,168,76,0.35)",
          }}
        >
          <span style={{ fontSize: "0.65rem", letterSpacing: "0.25em", fontFamily: "'DM Sans', sans-serif", textTransform: "uppercase" }}>
            {t("scroll_down")}
          </span>
          <ChevronDown size={14} strokeWidth={1} />
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────── */}
      <section
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "6rem 1.5rem",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "4rem" }}>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "0.68rem",
              letterSpacing: "0.3em",
              color: "rgba(201,168,76,0.55)",
              textTransform: "uppercase",
              marginBottom: "1.25rem",
            }}
          >
            {t("powered_by")} Gemini & Sora 2
          </p>
          <h2
            className="font-logo"
            style={{
              fontSize: "clamp(1.6rem, 3.5vw, 2.5rem)",
              fontWeight: 400,
              color: "#EDE0C4",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            {t("tab_image")} & {t("tab_video")}
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1px",
            background: "rgba(201,168,76,0.08)",
          }}
        >
          {[
            {
              icon: "✦",
              title: lang === "ar" ? "توليد الصور" : "Image Generation",
              desc:
                lang === "ar"
                  ? "حوّل كلماتك إلى صور مذهلة بنماذج Gemini Nano Banana."
                  : "Transform text prompts into stunning visuals with Gemini Nano Banana.",
            },
            {
              icon: "◈",
              title: lang === "ar" ? "توليد الفيديو" : "Video Generation",
              desc:
                lang === "ar"
                  ? "أنشئ مقاطع فيديو سينمائية من وصف نصي بقوة Sora 2."
                  : "Create cinematic video from text descriptions powered by Sora 2.",
            },
            {
              icon: "◎",
              title: lang === "ar" ? "تحكم إبداعي" : "Creative Control",
              desc:
                lang === "ar"
                  ? "تحكم في الأسلوب البصري والصوت والحركة والأبعاد بسهولة."
                  : "Control visual style, audio, motion, aspect ratio, and more.",
            },
          ].map((f) => (
            <div
              key={f.title}
              style={{
                padding: "2.5rem 2rem",
                background: "#120904",
              }}
            >
              <div
                style={{
                  fontSize: "1.1rem",
                  color: "#C9A84C",
                  marginBottom: "1.25rem",
                  opacity: 0.7,
                }}
              >
                {f.icon}
              </div>
              <h3
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "1rem",
                  color: "#EDE0C4",
                  marginBottom: "0.75rem",
                  fontWeight: 500,
                  letterSpacing: "0.04em",
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.88rem",
                  color: "#B8956A",
                  lineHeight: 1.75,
                  fontWeight: 300,
                }}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div style={{ textAlign: "center", marginTop: "4.5rem" }}>
          <button
            data-testid="section-cta-btn"
            className="btn-primary"
            style={{ padding: "1rem 3.5rem", fontSize: "0.85rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
            onClick={() => navigate("/studio")}
          >
            {t("hero_cta")}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          borderTop: "1px solid rgba(201,168,76,0.1)",
          padding: "2.5rem 1.5rem",
          textAlign: "center",
          background: "#0A0502",
        }}
      >
        <span
          className="font-logo"
          style={{
            fontSize: "1rem",
            letterSpacing: "0.35em",
            color: "#C9A84C",
            textTransform: "uppercase",
            fontWeight: 400,
          }}
        >
          KHATIRA
        </span>
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", color: "rgba(184,149,106,0.4)", marginTop: "0.5rem", letterSpacing: "0.1em" }}>
          khatira.ai — {t("free_label")}
        </p>
      </footer>
    </div>
  );
}
